import React from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  ButtonGhostV2,
  ButtonSecondaryCompact,
  UI_BODY_SM_DF,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION_DF,
  UI_TAG,
  UI_TAG_CONDENSED,
  colors2,
  clampText,
  typoV2_CSS,
  TagDisplayV2 as TagDisplayRaw,
  UI_CAPTION_SEMIBOLD_DF,
} from "oolib";
import { CaretUpDown } from "@phosphor-icons/react";
import { List as ListRaw } from "../../../components/UI/List";

const List = ListRaw as React.FC<any>;
import { t } from "../theme";
const TagDisplay = TagDisplayRaw as React.FC<any>;
import { ButtonTertiaryCompact } from "./Button";
import {
  Lightbox,
  LightboxTrigger,
  LightboxContent,
  LightboxContext,
} from "../../../components/UI/lightbox";
import {
  useGetTypeCategoryAndTags,
  convertTagDocsDataToOptions,
} from "../../../components/inputs/DynamicTagsInputs/utils";
import AnnoLightboxRaw from "../../../components/inputs/TextEditor/LexicalTextEditor/plugins/AnnoPlugin/AnnoLightbox";
import LexicalTextEditorRaw from "../../../components/inputs/TextEditor/LexicalTextEditor";

const LexicalTextEditor = LexicalTextEditorRaw as React.FC<any>;
const AnnoLightbox = AnnoLightboxRaw as React.FC<any>;
import { useAnnoEditorRegistry } from "../../../components/inputs/TextEditor/LexicalTextEditor/plugins/AnnoPlugin/annoEditorRegistry";
import { useCollabEditContext } from "../../../contexts/collabEditContext";

export type AnnoCardSection = "attribute" | "thematic" | "ner";

export type AnnoCardTag = {
  label: string;
  tagType?: string;
  _id?: string;
  subThemeLabel?: string;
  section?: AnnoCardSection;
};

export type AnnoCardDetailValue =
  | string
  | {
      display: string;
      _id?: string;
      subThemeLabel?: string;
      section?: AnnoCardSection;
    };

export type AnnoCardDetail = {
  label: string;
  values: AnnoCardDetailValue[];
  tagType?: string;
};

export type AnnoCardProps = {
  /** The annotation's dataId — used by the page-level controller to locate
   *  which rich-text field hosts the mark for update/delete operations. */
  dataId?: string;
  quote: any;
  /** Split-flow AI annotations carry a gist — a one-sentence reported-speech
   *  summary of the quote with all referents resolved. Rendered under the
   *  (clamped) quote as the scannable takeaway; absent on human/legacy annos. */
  gist?: string;
  /** Split-flow AI annotations carry an observation title — a 3-7 word
   *  encapsulation of the annotated text's meaning. Rendered above the
   *  gist in semibold same-font style; absent on human/legacy annos. */
  observationTitle?: string;
  /** Split-flow AI annotations carry the speaker's name/label (customer or
   *  facilitator) that C1 read from the transcript. Rendered as
   *  "${actorName}:" ABOVE the quote in semibold — matches transcript-style
   *  speaker attribution. Absent on human/legacy annos. */
  actorName?: string;
  tags: AnnoCardTag[];
  emoji?: React.ReactNode;
  selected: boolean;
  onSelect?: () => void;
  onCollapse?: () => void;
  details?: AnnoCardDetail[];
  onAddTag?: () => void;
  onAutoTag?: () => void;
  /** Global "TAG DETAILS" expand/collapse preference, lifted to the page so all
   *  cards share one source of truth (useLocalStorage is per-instance and would
   *  otherwise desync between mounted cards). */
  tagDetailsExpanded?: boolean;
  onToggleTagDetails?: () => void;
  /** Hide the tag chips + tag-details sections entirely. For hosts that render
   *  the card outside its home page where tag chips are redundant or their
   *  lookups unavailable (e.g. the Topics-tab annotation side panel). */
  hideTags?: boolean;
  /** Hide the selected-state action row (Add/Remove Tags, delete). For hosts
   *  outside the published page, where there's no editor registry to act on
   *  — the card can still expand to show its quote and tag details. */
  hideActions?: boolean;
};

const Card = styled.div<{ $selected: boolean }>`
  position: relative;
  border-radius: ${({ $selected }) => ($selected ? "1.2rem" : "0.6rem")};
  cursor: ${({ $selected }) => ($selected ? "default" : "pointer")};
  background: ${({ $selected }) => ($selected ? t("surface1") : t("grey5"))};
  border: 1px solid
    ${({ $selected }) => ($selected ? t("grey80") : "transparent")};
  box-shadow: ${({ $selected }) =>
    $selected ? `0 4px 10px ${t("shadowColor")}` : "none"};
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, border-radius 0.2s ease;
  overflow: hidden;
`;

const ActionsReveal = styled(motion.div)`
  overflow: hidden;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem;
`;

/* Plain div on purpose: every visible glyph in here lives inside the Lexical
   renderer's .contentEditableLex, which applies its OWN typography/ink —
   type styles on the wrapper itself never reach the text (the previous
   styled(UI_BODY_SM_DF) base was inert). Styling happens by pushing rules
   onto the editor's class from this wrapper: the scoped selector outweighs
   the editor's bare class selector. */
const Quote = styled.div<{ $clamped?: boolean }>`
  .contentEditableLex,
  .contentEditableLex p {
    ${typoV2_CSS.UI_CAPTION_DF};
    color: ${colors2.grey60};
    font-style: italic;
  }
  /* The editor forces 22px line boxes on p > span (!important) — sized for
     UI_BODY_SM. Re-force a proportional line-height so the caption-sized
     quote doesn't ride on oversized lines. */
  .contentEditableLex p span {
    line-height: 1.5 !important;
  }
  /* Compact (unselected) cards show the quote as a 2-line teaser — the gist
     below carries the takeaway. Selecting the card reveals the full quote.
     The clamp sits on the wrapper around the read-only Lexical renderer;
     its paragraphs flow as one clamped box. */
  ${({ $clamped }) => ($clamped ? clampText(2) : "")}
`;

// Quote + gist read as one unit: verbatim evidence on top, resolved takeaway
// underneath — tighter than the card's 1rem section gap.
const QuoteBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Typographically identical to Quote (same Typo2 comp, same line-height) —
// only the ink differs: gist carries the primary color, quote is muted.
const Gist = styled(UI_BODY_SM_DF)`
  color: ${t("primary")};
  line-height: 1.55;
`;

// Observation title — 3-7 word encapsulation of the annotation's meaning.
// Same base as Gist, semibold weight; sits directly above the gist as a
// scannable header for the resolved-meaning line.
const ObservationTitle = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: ${t("primary")};
  line-height: 1.55;
`;

// Speaker attribution rendered above the quote in transcript-style ("Name:").
// Same Typo2 sizing + line-height as Quote/Gist — no weight/color emphasis;
// the "Name:" formatting alone signals attribution, in the annotation's own
// text style.
const ActorLabel = styled(UI_CAPTION_DF)`
  /* line-height: 1.55; */
  font-style: italic;
  color: ${colors2.grey60};
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
`;

// Each chip's flex item. min-width:0 lets an over-long chip shrink to the row
// width (rather than forcing its full content width and overflowing), which is
// what lets the tag pill inside truncate with an ellipsis. max-width caps it at
// the row so it never exceeds the card.
const ChipItem = styled.span`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
`;

const Divider = styled.div`
  height: 1px;
  background: ${t("grey10")};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.9rem 1.2rem 0.9rem 1.4rem;
`;

const TrashSlot = styled.div`
  margin-left: auto;
`;

// "TAG DETAILS" expand/collapse trigger (focused view). The whole row is the
// hit target; caret flips with the persisted expanded state.
const TagDetailsTrigger = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const TagDetailsLabel = styled(UI_TAG_CONDENSED)`
  color: ${colors2.grey60};
`;

// Stacks the labelled groups (expanded) with a dashed rule between each.
const TagGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const TagGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const GroupLabel = styled(UI_CAPTION_DF)`
  color: ${colors2.grey40};
`;

const DashedDivider = styled.div`
  border-top: 1px dashed ${t("grey20")};
`;

const normalizeDetailValue = (
  v: AnnoCardDetailValue,
): {
  display: string;
  _id?: string;
  subThemeLabel?: string;
  section?: AnnoCardSection;
} =>
  typeof v === "string"
    ? { display: v }
    : {
        display: v.display,
        _id: v._id,
        subThemeLabel: v.subThemeLabel,
        section: v.section,
      };

type GroupOption = {
  _id: string;
  value: string;
  display: string;
  tagId?: string;
  meta?: any;
  loading?: boolean;
};

// Match AsideFilters / ListGroup heading style: oolib's UI_TAG typography
// already encodes the weight/tracking/uppercase, paired with grey50 + the
// standard padding.
const GroupHeader = styled(UI_TAG)`
  padding: 0.6rem 0.8rem 0.4rem;
  color: ${colors2.grey50};
`;

/** Resolve a tag doc's parent group by scanning its `tags` field — the first
 *  non-empty collection wins. Mirrors TagsManagerV2's child→parent lookup
 *  (FlexibleColumnTagManager/index.js:292). Returns null for orphan tags. */
const findParentGroup = (
  doc: any,
): { parentCategory: string; parentRef: any } | null => {
  const tagsObj = doc?.tags;
  if (!tagsObj || typeof tagsObj !== "object") return null;
  for (const cat of Object.keys(tagsObj)) {
    const arr = tagsObj[cat]?.data;
    if (Array.isArray(arr) && arr.length > 0) {
      return { parentCategory: cat, parentRef: arr[0] };
    }
  }
  return null;
};

/** Group-scoped tag editor. Opens when a chip on the AnnoCard is clicked.
 *
 *  Behaviour: shows every sibling of the clicked tag (resolved via its parent
 *  group in the taxonomy), pre-checks the ones already applied to this
 *  annotation, and on Done commits a diff'd full tagsPayload via the editor's
 *  own handler (registry handle). Tags outside this group are untouched.
 *
 *  Orphan fallback: if the clicked tag has no parent, the list degrades to a
 *  single-option toggle for just that tag (so user can still remove it).
 */
const GroupTagPicker: React.FC<{
  dataId: string;
  clickedTagId: string;
  clickedTagType: string;
  onClose: () => void;
}> = ({ dataId, clickedTagId, clickedTagType, onClose }) => {
  const registry = useAnnoEditorRegistry();
  const handle = registry?.find(dataId);
  const currentAnnoTags = handle?.getTags(dataId) ?? {};

  // Hold the collab edit-lock while this group picker is open — the same lock
  // the in-editor edit flow acquires. Released on close.
  React.useEffect(() => {
    if (!handle) return;
    handle.beginEdit(dataId);
    return () => {
      handle.endEdit();
    };
  }, [handle, dataId]);

  const { data, status } = useGetTypeCategoryAndTags({
    tagTypeProp: clickedTagType,
    enableDataFetch: !!clickedTagType,
  } as any);

  const { groupHeader, groupOptions } = React.useMemo(() => {
    if (status !== "success" || !Array.isArray(data)) {
      // List renders per-option skeletons when option.loading is true
      // (ListItem.jsx:89-103). Passing a non-empty array of loading
      // placeholders avoids the "no results" flash before the fetch lands.
      const placeholders = Array.from({ length: 6 }, (_, i) => ({
        _id: `__loading-${i}`,
        value: `__loading-${i}`,
        display: "",
        loading: true,
      })) as GroupOption[];
      return { groupHeader: "", groupOptions: placeholders };
    }
    const docs = data as any[];
    const clickedDoc = docs.find((d) => d._id === clickedTagId);
    if (!clickedDoc) return { groupHeader: "", groupOptions: [] as GroupOption[] };

    const parent = findParentGroup(clickedDoc);
    // Raw tag docs don't always carry a top-level `.display` — `convertTag-
    // DocsDataToOptions` handles the fallback chain (display → name →
    // main.title…). Route both the orphan single-option case and the sibling
    // list through it so the picker always has a resolved label.
    if (!parent) {
      const [opt] = convertTagDocsDataToOptions([clickedDoc], "tags") as any[];
      return {
        groupHeader: opt?.display ?? "",
        groupOptions: opt
          ? [{ _id: opt._id, value: opt._id, display: opt.display, tagId: opt.tagId, meta: opt.meta }]
          : [],
      };
    }
    const { parentCategory, parentRef } = parent;
    const siblings = docs.filter((d) => {
      const arr = d?.tags?.[parentCategory]?.data;
      return Array.isArray(arr) && arr.some((p: any) => p._id === parentRef._id);
    });
    const siblingOptions = convertTagDocsDataToOptions(siblings, "tags") as any[];
    return {
      groupHeader: parentRef.display ?? "",
      groupOptions: siblingOptions.map((s) => ({
        _id: s._id,
        value: s._id,
        display: s.display,
        tagId: s.tagId,
        meta: s.meta,
      })),
    };
  }, [data, status, clickedTagId]);

  const initialSelection = React.useMemo(() => {
    const arr: any[] = currentAnnoTags?.[clickedTagType]?.data ?? [];
    const ids = new Set(groupOptions.map((g: GroupOption) => g._id));
    return arr
      .filter((d) => ids.has(d._id))
      .map((d) => ({ ...d, value: d._id }));
  }, [currentAnnoTags, clickedTagType, groupOptions]);

  const [selection, setSelection] = React.useState<any[]>(initialSelection);
  // Resync when options arrive after first paint (the tag-doc fetch is async).
  React.useEffect(() => setSelection(initialSelection), [initialSelection]);

  const handleDone = () => {
    // Bail without writes if Done is hit before the fetch resolved — the
    // diff would treat the user's real tags as "not in this group" and
    // strip nothing, but also wouldn't add anything; harmless but pointless.
    if (!handle || status !== "success") {
      onClose();
      return;
    }
    const currentBlock = currentAnnoTags?.[clickedTagType];
    const currentArr: any[] = currentBlock?.data ?? [];
    const groupIds = new Set(groupOptions.map((g: GroupOption) => g._id));
    const keep = currentArr.filter((d) => !groupIds.has(d._id));
    const additions = selection.map((s) => ({
      _id: s._id,
      display: s.display,
      tagId: s.tagId,
      ...(s.meta ? { meta: s.meta } : {}),
    }));
    const newData = [...keep, ...additions];
    const next: Record<string, any> = { ...currentAnnoTags };
    if (newData.length === 0) {
      delete next[clickedTagType];
    } else {
      next[clickedTagType] = {
        collectionId: currentBlock?.collectionId ?? clickedTagType,
        data: newData,
      };
    }
    // Same editor handler the in-editor edit flow runs — persists via onChange
    // → annotationEditPipeline. Releases the lock held since open.
    handle.confirmEdit({ data: next, dataId });
    onClose();
  };

  return (
    <LightboxContext.Provider value={null}>
      {groupHeader && <GroupHeader>{groupHeader}</GroupHeader>}
      <List
        options={groupOptions}
        value={selection}
        isMulti
        invert
        disableShadow
        // `List` in multi mode fires onChange per clicked option (see
        // useDragSelect.handleItemChange) — the parent must toggle the array.
        onChange={(_id: string, option: any) => {
          setSelection((prev) =>
            prev.some((s) => s.value === option.value)
              ? prev.filter((s) => s.value !== option.value)
              : [...prev, option],
          );
        }}
      />
      <div style={{ padding: "0.4rem 1.2rem 1.2rem", display: "flex", justifyContent: "center" }}>
        <ButtonSecondaryCompact
          icon="Check"
          iconWeight="bold"
          value="Done"
          onClick={handleDone}
          style={{ width: "100%" }}
        />
      </div>
    </LightboxContext.Provider>
  );
};

export const AnnoCard: React.FC<AnnoCardProps> = ({
  dataId,
  quote,
  gist,
  observationTitle,
  actorName,
  tags,
  selected,
  onSelect,
  details,
  onAddTag,
  onAutoTag,
  tagDetailsExpanded = true,
  onToggleTagDetails,
  hideTags = false,
  hideActions = false,
}) => {
  const registry = useAnnoEditorRegistry();
  const handleCardClick = () => {
    // Fire on every click, including when already selected: re-selecting
    // re-centers this card and scrolls its mark back into view if the user had
    // scrolled either away. Interactive children (chips, actions, tag-details
    // toggle) stopPropagation, so they don't trigger this.
    if (onSelect) onSelect();
  };

  // Collab: disable this card's tag-edit + delete affordances when another
  // collaborator holds the lock on the field that hosts this annotation. The
  // in-editor path is already blocked by CollabFieldWrapper's lock overlay;
  // the sidebar card sits outside that overlay, so it must gate itself. The
  // field's valuePath comes from the (stable) editor handle; the (changing)
  // lock state is read reactively from the page-level CollabEditContext, so
  // the buttons re-enable the moment the other editor releases. Null context
  // (collab off / non-collab tenant) → never locked → no behaviour change.
  const collabEdit = useCollabEditContext();
  const fieldValuePath = dataId ? registry?.find(dataId)?.getValuePath?.() : undefined;
  const fieldLockedByOther = !!(
    collabEdit &&
    fieldValuePath &&
    collabEdit.locks?.[fieldValuePath] &&
    collabEdit.locks[fieldValuePath].socketId !== collabEdit.socketId
  );

  // Card→mark highlight: when this card is selected, highlight its mark(s) in
  // the body via the editor handle (imperative — bypasses the memoized
  // ViewBlockGenerator that drops reactive props). Cleared on deselect/unmount.
  React.useEffect(() => {
    if (!dataId || !selected) return;
    const handle = registry?.find(dataId);
    handle?.focusMark?.(dataId, true);
    return () => handle?.focusMark?.(dataId, false);
  }, [registry, dataId, selected]);

  // detailValues come from attribute-role buckets, tagValues from thematic-role
  // tags — so each defaults to that section when the page didn't resolve one
  // (e.g. a tag missing from the fetched taxonomy). An explicit `section` from
  // the data wins, which is how Named Entities get pulled out of either bucket.
  const detailValues = (details ?? []).flatMap((d) =>
    d.values.map((v) => {
      const n = normalizeDetailValue(v);
      return { ...n, tagType: d.tagType, section: n.section ?? ("attribute" as AnnoCardSection) };
    }),
  );
  const tagValues = tags.map((tg) => ({
    display: tg.label,
    tagType: tg.tagType,
    _id: tg._id,
    subThemeLabel: tg.subThemeLabel,
    section: tg.section ?? ("thematic" as AnnoCardSection),
  }));

  const allChips = [...detailValues, ...tagValues];

  // Three dashed-divider sections, in fixed order (thematic → attribute → ner).
  // Within each, chips are grouped under their subTheme label (insertion order
  // preserved), except thematics where the "Signals" group always leads. Empty
  // sections/groups are dropped at render time.
  const chipSections = React.useMemo(() => {
    const order: AnnoCardSection[] = ["thematic", "attribute", "ner"];
    return order.map((section) => {
      const chips = allChips.filter((c) => c.section === section);
      const groups: { label: string; chips: typeof allChips }[] = [];
      const idx = new Map<string, number>();
      for (const c of chips) {
        const label = c.subThemeLabel || "Other";
        if (!idx.has(label)) {
          idx.set(label, groups.length);
          groups.push({ label, chips: [] });
        }
        groups[idx.get(label)!].chips.push(c);
      }
      // Signals always first within thematics; sort is stable so the rest keep
      // their insertion order.
      if (section === "thematic") {
        groups.sort(
          (a, b) =>
            (a.label.toLowerCase() === "signals" ? 0 : 1) -
            (b.label.toLowerCase() === "signals" ? 0 : 1),
        );
      }
      return { section, groups };
    }).filter((s) => s.groups.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, tags]);

  // Tracks which chip's group-picker lightbox is open. One at a time across
  // both rows; clicking another chip swaps which picker is mounted.
  const [openChipKey, setOpenChipKey] = React.useState<string | null>(null);

  // "Add/Remove Tags" lightbox state — the SAME (now editor-decoupled)
  // AnnoLightbox the in-editor edit flow uses, anchored below the Add Tag
  // button. Persists through the editor's own handleConfirmEdit via the
  // registry handle resolved on open.
  const addTagBtnRef = React.useRef<HTMLDivElement | null>(null);
  const [addTagOpen, setAddTagOpen] = React.useState(false);
  const [addTagData, setAddTagData] = React.useState<Record<string, any>>({});
  const [addTagPos, setAddTagPos] = React.useState<{ x: number; y: number } | null>(null);
  const addTagHandleRef = React.useRef<any>(null);

  const closeAddTag = () => {
    addTagHandleRef.current?.endEdit?.();
    addTagHandleRef.current = null;
    setAddTagOpen(false);
    setAddTagData({});
    setAddTagPos(null);
  };

  const openAddTag = async () => {
    onAddTag?.();
    if (!dataId) return;
    if (fieldLockedByOther) return; // field held by another collaborator

    const handle = registry?.find(dataId);
    if (!handle) return;
    // Acquire the collab edit-lock first (same gate as the in-editor edit).
    const ok = await handle.beginEdit(dataId);
    if (!ok) return; // another user holds the lock
    addTagHandleRef.current = handle;
    // Pre-populate with the annotation's current tags so the user sees what's
    // already applied and edits from that set.
    setAddTagData(handle.getTags(dataId) ?? {});
    const rect = addTagBtnRef.current?.getBoundingClientRect();
    if (rect) setAddTagPos({ x: rect.x + rect.width / 2, y: rect.bottom + 5 });
    setAddTagOpen(true);
  };

  const renderChip = (
    v: { display: string; tagType?: string; _id?: string; subThemeLabel?: string },
    keyPrefix: string,
    i: number,
  ) => {
    const key = `${keyPrefix}-${v._id ?? v.display}-${i}`;
    const isOpen = openChipKey === key;
    const canEdit = !!(dataId && v._id && v.tagType) && !fieldLockedByOther;
    return (
      <ChipItem key={key} onClick={(e) => e.stopPropagation()}>
        <Lightbox open={isOpen} onOpenChange={(o) => canEdit && setOpenChipKey(o ? key : null)}>
          <LightboxTrigger>
            {/* Long labels wrap to 2 lines then ellipsize (textWrapMaxLines);
                no tooltip wrapper — it sat between the chip and
                LightboxTrigger and swallowed the click that opens
                GroupTagPicker. */}
            <TagDisplay display={v.display} active={isOpen} textWrapMaxLines={2} />
          </LightboxTrigger>
          <LightboxContent className="js-anno-keep-focus">
            {isOpen && canEdit && (
              <GroupTagPicker
                dataId={dataId!}
                clickedTagId={v._id!}
                clickedTagType={v.tagType!}
                onClose={() => setOpenChipKey(null)}
              />
            )}
          </LightboxContent>
        </Lightbox>
      </ChipItem>
    );
  };

  return (
    <Card $selected={selected} onClick={handleCardClick}>
      <Inner>
        <QuoteBlock>
          <div>
            {actorName && <ActorLabel>{`${actorName}:`}</ActorLabel>}
            <Quote $clamped={!selected}>
              <LexicalTextEditor
                variant="rich"
                readOnly
                v2
                disableViewHighlights
                disableCreateHighlights
                value={{ isLexical: true, editorState: quote }}
              />
            </Quote>
          </div>
          {observationTitle && <ObservationTitle>{observationTitle}</ObservationTitle>}
          {gist && <Gist>{gist}</Gist>}
        </QuoteBlock>
        {hideTags ? null : !selected ? (
          <>
            {chipSections.map((s) => (
              <TagsRow key={s.section}>
                {s.groups
                  .flatMap((g) => g.chips)
                  .map((v, i) => renderChip(v, `u${s.section}`, i))}
              </TagsRow>
            ))}
          </>
        ) : (
          allChips.length > 0 && (
            <>
              <Divider />
              <div>
              <TagDetailsTrigger
                type="button"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onToggleTagDetails?.();
                }}
              >
                <CaretUpDown size={16} weight="bold" color={colors2.grey50} />
                <TagDetailsLabel>Tag Details</TagDetailsLabel>
              </TagDetailsTrigger>
              <div style={{ marginTop: "0.8rem" }}>
                {tagDetailsExpanded ? (
                  <TagGroups>
                    {chipSections.map((s, si) => (
                      <React.Fragment key={s.section}>
                        {si > 0 && <DashedDivider />}
                        {s.groups.map((g, gi) => (
                          <TagGroup key={`${s.section}-${g.label}`}>
                            <GroupLabel>{g.label}</GroupLabel>
                            <TagsRow>
                              {g.chips.map((v, i) => renderChip(v, `${s.section}${gi}`, i))}
                            </TagsRow>
                          </TagGroup>
                        ))}
                      </React.Fragment>
                    ))}
                  </TagGroups>
                ) : (
                  <TagGroups>
                    {chipSections.map((s) => (
                      <TagsRow key={s.section}>
                        {s.groups
                          .flatMap((g) => g.chips)
                          .map((v, i) => renderChip(v, `c${s.section}`, i))}
                      </TagsRow>
                    ))}
                  </TagGroups>
                )}
              </div>
              </div>
            </>
          )
        )}
      </Inner>

      <AnimatePresence initial={false}>
        {selected && !hideActions && (
          <ActionsReveal
            key="actions"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.27, ease: [0.32, 0.72, 0, 1] }}
          >
            <Divider />
            <Actions onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <div ref={addTagBtnRef} style={{ display: "inline-flex" }}>
                <ButtonTertiaryCompact
                  icon="Plus"
                  iconWeight="bold"
                  disabled={fieldLockedByOther}
                  onClick={openAddTag}
                >
                  Add/Remove Tags
                </ButtonTertiaryCompact>
              </div>
              {/* <ButtonTertiaryCompact
                icon="Sparkle"
                iconWeight="bold"
                onClick={onAutoTag}
              >
                Auto Tag
              </ButtonTertiaryCompact> */}
              <TrashSlot>
                <ButtonGhostV2
                  icon="Trash"
                  iconColor={t("grey80")}
                  disabled={fieldLockedByOther}
                  onClick={() => {
                    // Blocked while another collaborator holds the field — the
                    // remove would mutate our editorState locally but never
                    // reach the Y.Map (onChange isn't routed to setField on a
                    // lockedByOther field), diverging until reload.
                    if (fieldLockedByOther) return;
                    // Same editor handler the in-editor remove runs (unwraps
                    // the mark, persists via onChange → pipeline).
                    if (dataId) registry?.find(dataId)?.remove(dataId);
                  }}
                />
              </TrashSlot>
            </Actions>
          </ActionsReveal>
        )}
      </AnimatePresence>

      {addTagOpen && addTagPos && (
        <AnnoLightbox
          mode="edit"
          dataId={dataId}
          data={addTagData}
          setData={({ tagType, value }: { tagType: string; value: any }) =>
            setAddTagData((prev) => {
              const next: Record<string, any> = { ...prev };
              if (value?.data?.length) next[tagType] = value;
              else delete next[tagType];
              return next;
            })
          }
          annoConfig={addTagHandleRef.current?.getAnnoConfig?.()}
          position={addTagPos}
          readOnly
          allowEdit
          activeData_with_relatedNodeKeys={[]}
          handleClickOutside={closeAddTag}
          handleCancel={closeAddTag}
          handleConfirmEdit={({ data, dataId: id }: { data: any; dataId: string }) => {
            addTagHandleRef.current?.confirmEdit({ data, dataId: id });
            closeAddTag();
          }}
          handleConfirmCreate={() => {}}
          handleRemove={() => {}}
          setEditMode={() => {}}
        />
      )}
    </Card>
  );
};

export default AnnoCard;
